import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, normalizePhone } from "@/lib/api";
import { PhotoPicker } from "@/components/PhotoPicker";
import { ClerkPinModal } from "@/components/ClerkPinModal";
import { DateTimeQuickPicker } from "@/components/DateTimeQuickPicker";
import { CourierSelect } from "@/components/CourierSelect";

const EMPTY = {
  delivery_time: "",
  full_name: "",
  phone: "",
  address: "",
  items: "",
  notes: "",
  amount: "",
  claimed_by_id: "",
};

export default function NewOrderModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [autofilled, setAutofilled] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);

  // Telefon -> müşteri otomatik doldur
  useEffect(() => {
    const norm = normalizePhone(form.phone);
    if (norm.length < 10) { setAutofilled(false); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/customers/by-phone/${norm}`);
        if (cancelled) return;
        setForm((f) => ({
          ...f,
          full_name: f.full_name || data.full_name || "",
          address: f.address || data.address || "",
        }));
        setAutofilled(true);
        toast.success(`${data.full_name} kayıtlı müşteri — bilgiler dolduruldu`);
      } catch { setAutofilled(false); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line
  }, [form.phone]);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleImage = (file, preview) => {
    setImageFile(file);
    setImagePreview(preview);
  };

  const reset = () => {
    setForm({ ...EMPTY });
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Zorunlu alanlar: teslim saati + götürecek kurye. (PIN ekranı her zaman açılır.)
    if (!form.delivery_time || !form.delivery_time.includes("T") || form.delivery_time.endsWith("T")) {
      toast.error("Teslim saati seçilmeden sipariş kaydedilemez");
      return;
    }
    if (!form.claimed_by_id) {
      toast.error("Götürecek kurye seçin");
      return;
    }
    setPinOpen(true);
  };

  const submitOrder = async (clerkPin) => {
    const items = (form.items || "").split("\n").map((s) => s.trim()).filter(Boolean);
    const notes = (form.notes || "").split("\n").map((s) => s.trim()).filter(Boolean);
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("delivery_time", new Date(form.delivery_time).toISOString());
      fd.append("full_name", form.full_name.trim());
      fd.append("phone", normalizePhone(form.phone));
      fd.append("address", form.address.trim());
      fd.append("description", ""); // legacy field unused
      fd.append("items", JSON.stringify(items));
      fd.append("notes", JSON.stringify(notes));
      fd.append("amount", form.amount ? parseFloat(form.amount).toString() : "0");
      fd.append("claimed_by_id", form.claimed_by_id);
      fd.append("clerk_pin", clerkPin);
      if (imageFile) fd.append("image", imageFile);
      const { data } = await api.post("/orders", fd);
      toast.success(`Sipariş kaydedildi · ${data.created_by_name}`);
      reset();
      setPinOpen(false);
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err));
      // PIN modalı açık kalsın; kullanıcı tekrar deneyebilsin
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="new-order-modal"
      className="fixed inset-0 z-50 bg-zinc-950/60"
      onClick={onClose}
    >
      {/* Scroll container — uses native page-style scrolling so iOS Safari
          always lets users reach the top of the form, even when the on-screen
          keyboard is open. */}
      <div className="absolute inset-0 overflow-y-auto overscroll-contain">
        <div className="min-h-full flex items-start sm:items-center justify-center p-0 sm:p-6">
          <div
            className="bg-white border-2 border-zinc-950 brutal-shadow-dark w-full sm:max-w-lg my-0 sm:my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between border-b-2 border-zinc-200 px-5 py-3 sticky bg-white z-10"
              style={{ top: 0, paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-zinc-950 text-white flex items-center justify-center">
                  <Plus strokeWidth={3} size={16} />
                </div>
                <h2 className="text-lg font-black tracking-tight text-zinc-950">Yeni Sipariş</h2>
              </div>
              <button data-testid="close-new-order" onClick={onClose} className="p-2 text-zinc-500 hover:text-zinc-950">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-3 pb-[max(1.25rem,env(safe-area-inset-bottom))]" data-testid="new-order-form">
          <Field label="Götürecek Kişi *">
            <CourierSelect
              value={form.claimed_by_id}
              onChange={(id) => update("claimed_by_id", id)}
              testIdPrefix="form-claim"
            />
          </Field>

          <div className="bg-amber-50 border-2 border-amber-300 p-3 space-y-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-900 font-bold">
              Teslim Edilecek Tarih ve Saat *
            </div>
            <DateTimeQuickPicker
              value={form.delivery_time}
              onChange={(v) => update("delivery_time", v)}
              testIdPrefix="form-delivery"
            />
            <div className="font-mono text-[10px] uppercase tracking-wider text-amber-700">
              Tarih bugünün tarihi — saat 15 dk aralıkta seçilir, boş bırakılamaz
            </div>
          </div>

          <Field label="İsim Soyisim">
            <input
              data-testid="form-full-name"
              type="text"
              autoComplete="name"
              autoCapitalize="words"
              enterKeyHint="next"
              className="brutalist-input"
              value={form.full_name}
              onChange={(e) => update("full_name", e.target.value)}
            />
          </Field>

          <Field label="Telefon">
            <input
              data-testid="form-phone"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              enterKeyHint="next"
              className="brutalist-input"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
            />
            {autofilled && (
              <div data-testid="autofill-indicator" className="font-mono text-[10px] uppercase tracking-wider text-emerald-700 mt-1">
                ✓ Kayıtlı müşteri — bilgiler otomatik dolduruldu
              </div>
            )}
          </Field>

          <Field label="Adres">
            <textarea
              data-testid="form-address"
              rows={2}
              autoComplete="street-address"
              autoCapitalize="sentences"
              enterKeyHint="next"
              className="brutalist-input resize-none"
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
            />
          </Field>

          <Field label="Ürünler — Her satıra bir ürün">
            <textarea
              data-testid="form-items"
              rows={4}
              autoCapitalize="sentences"
              className="brutalist-input resize-none font-mono text-[15px] leading-relaxed"
              value={form.items}
              onChange={(e) => update("items", e.target.value)}
            />
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-1">
              Enter ile yeni ürün satırı açın
            </div>
          </Field>

          <Field label="Açıklamalar / Özel Notlar — Her satıra bir not">
            <textarea
              data-testid="form-notes"
              rows={3}
              autoCapitalize="sentences"
              className="brutalist-input resize-none font-mono text-[15px] leading-relaxed"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
            />
          </Field>

          <Field label="Tutar (₺) — Opsiyonel">
            <input
              data-testid="form-amount"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              className="brutalist-input"
              value={form.amount}
              onChange={(e) => update("amount", e.target.value)}
            />
          </Field>

          <Field label="Sipariş Kağıdı Fotoğrafı (Opsiyonel)">
            <PhotoPicker
              file={imageFile}
              preview={imagePreview}
              onChange={handleImage}
              testIdPrefix="new-order-photo"
              cameraLabel="Sipariş Kağıdı Fotoğrafı Çek"
              galleryLabel="Galeriden Sipariş Kağıdı Seç"
            />
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-2">
              Kağıda yazılmış sipariş notunun fotoğrafını ekleyin
            </div>
          </Field>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              data-testid="cancel-new-order"
              onClick={onClose}
              className="flex-1 py-3 border-2 border-zinc-300 text-zinc-700 text-sm font-bold uppercase tracking-widest hover:border-zinc-950 hover:text-zinc-950 transition-colors"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              data-testid="submit-new-order"
              disabled={submitting}
              className="flex-1 bg-zinc-950 text-white text-sm font-black uppercase tracking-widest py-3 border-b-4 border-black active:border-b-0 active:translate-y-[3px] transition-all disabled:opacity-60"
            >
              {submitting ? "Kaydediliyor…" : "Siparişi Kaydet"}
            </button>
          </div>
        </form>
          </div>
        </div>
      </div>

      <style>{`
        .brutalist-input {
          width: 100%;
          background-color: #fafafa;
          border: 2px solid #e4e4e7;
          padding: 0.7rem 0.75rem;
          font-size: 16px;  /* prevent iOS zoom on focus */
          color: #09090b;
          outline: none;
          transition: border-color 0.15s;
        }
        .brutalist-input:focus { border-color: #09090b; background-color: #ffffff; }
      `}</style>

      <ClerkPinModal
        open={pinOpen}
        busy={submitting}
        title="Siparişi Kaydet"
        submitLabel="Kaydet"
        onClose={() => { if (!submitting) setPinOpen(false); }}
        onSubmit={submitOrder}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
